/**
 * App binding for the shared chunk-aware state machine
 * (`@listening/frontend/audio-chunk-player/core`). The state machine itself is
 * platform-agnostic; this wrapper injects start's app-specific seams
 * (the auth session as an atom, and the doc-pack kill-switch flag) and
 * re-exports the shared surface so existing `@/lib/audio-chunk-player` import
 * paths keep working.
 */

import {
  AudioChunkPlayerProviderInternal as SharedAudioChunkPlayerProviderInternal,
  type DocPackLoadFailure,
} from '@listening/frontend/audio-chunk-player/core';
import { useAudioChunkActions } from '@listening/frontend/audio-chunk-player/hooks';
import * as Sentry from '@sentry/react';
import { useEffect, type PropsWithChildren } from 'react';
import { useDocPackLoadingEnabled } from '@/lib/doc-reader/pack/doc-pack-source';
import { userLoggedInAtom } from '@/services/auth-service';

export {
  type AudioChunkPlayerState,
  getDocPackPlaybackAnchorForPosition,
  type DocPackPlaybackAnchor,
  useCurrentAudioChunkQuery,
  useDocPack,
  useDocPackPageError,
  useDocumentSummary,
  usePreferedVoice,
} from '@listening/frontend/audio-chunk-player/core';

function ReconnectResumeBridge() {
  const { resumeAfterReconnect } = useAudioChunkActions();
  useEffect(() => {
    window.addEventListener('online', resumeAfterReconnect);
    return () => {
      window.removeEventListener('online', resumeAfterReconnect);
    };
  }, [resumeAfterReconnect]);
  return null;
}

/** Error level, so Sentry rates the issue high priority and its issue alert
 * notifies; grouped by outcome. */
function captureDocPackLoadFailure({
  audioId,
  outcome,
  pageNumber,
  error,
}: DocPackLoadFailure) {
  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('doc_pack.load_failure', outcome);
    scope.setContext('doc_pack', {
      audio_id: audioId,
      page_number: pageNumber ?? null,
    });
    scope.setFingerprint(['doc-pack-load-failure', outcome]);
    Sentry.captureException(error);
  });
}

export function AudioChunkPlayerProviderInternal({
  children,
  ...props
}: PropsWithChildren & {
  initialAudioId?: string;
  initialLookupAudioId?: string;
}) {
  // Same kill-switch source as every other web pack consumer (item #7 of
  // review-issues-1.md): the engine gates its doc-pack path on this AND the
  // pack version check.
  const docPackEnabled = useDocPackLoadingEnabled();

  return (
    <SharedAudioChunkPlayerProviderInternal
      {...props}
      userLoggedInAtom={userLoggedInAtom}
      docPackEnabled={docPackEnabled}
      onDocPackLoadFailure={captureDocPackLoadFailure}
    >
      <ReconnectResumeBridge />
      {children}
    </SharedAudioChunkPlayerProviderInternal>
  );
}
